import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import AppState from '@webapp/store/state';
import { UPDATE_ARTIST } from '@webapp/store/artist/actions';
import { UpdateArtist } from '@webapp/store/artist/action-creators';

import SimpleArtist from '@common/types/artist/simple-artist';

import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';

import ArtistSettingsGeneral from '@webapp/controllers/artist/artist-settings-general';
import { CloseMenu } from '@webapp/store/ui/action-creators';

import { validateArtistsPlatforms } from '@webapp/utils/validators/artist';

import { Subscription } from '@services/subscription/types/subscription';
import { SubscriptionItem } from '@common/types/subscription-manager/list-subscriptions-response';
import { TracPageSocialLink } from '@services/tracpage/types/track-page-social-link';
import { TracPage } from '@services/tracpage/types/trac-page';
import { UpdateTracPage } from '@webapp/store/tracpage/action-creators';

import SitePreview from './site-preview';
import { artistSiteUrl } from '@webapp/store/artist/selectors';
import { updatedArtistSideEffect } from '@webapp/utils/artists/update-artist-rudderstack';
import ButtonFixed from '@components/layout/button-fixed';
import Button from '@components/actions/button';

interface ArtistProfileInfoPageProps {
  className?: string;
  artist?: SimpleArtist;
  social: TracPageSocialLink[];
  isLoading: boolean;
  isMobile?: boolean;
  currentSubscriptions?: Subscription[];
  updateArtist: (artistData: SimpleArtist) => UpdateArtist;
  publishArtistTracPage: (page: TracPage) => UpdateTracPage<TracPage>;
  artistTracPage: TracPage;
  isArtistTracPageChanged: boolean;
  artistSiteUrl?: string;
  closeAllMenus: () => CloseMenu;
}

const mapStateToProps = (state: AppState) => {
  const foundSubscriptions = state.subscriptionManager.summary?.artists;
  const filterSubscriptions = foundSubscriptions?.map((val) => {
    return val.subscriptions;
  });
  let subItem: SubscriptionItem[] = [];
  filterSubscriptions?.forEach((val) => {
    val.forEach((val2) => {
      subItem.push(val2);
    });
  });
  let sub: Subscription[] = [];
  subItem?.map((val) => {
    sub.push(val.subscription);
  });
  const currentSubscriptions = sub;
  return {
    artist: state.artist.active,
    social: state.tracpage.artistPage.data.social,
    artistSiteUrl: artistSiteUrl(state, true),
    artistTracPage: state.tracpage.artistPage,
    isArtistTracPageChanged: state.tracpage.isArtistPageChanged,
    isLoading: !!state.ui.loading[UPDATE_ARTIST],
    isMobile: state.ui.mobile,
    currentSubscriptions,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  publishArtistTracPage: (page: TracPage) =>
    dispatch(new UpdateTracPage('tracpage', page)),
  updateArtist: (artistData: SimpleArtist) =>
    dispatch(new UpdateArtist(artistData)),
  closeAllMenus: () => dispatch(new CloseMenu()),
});

const validateArtist = (artistData?: SimpleArtist) => {
  return (
    artistData &&
    artistData.name &&
    validateArtistsPlatforms(artistData.platforms)
  );
};

export const ArtistProfileInfoPage: React.FC<ArtistProfileInfoPageProps> = (
  props
) => {
  const {
    artist,
    currentSubscriptions,
    artistTracPage,
    isArtistTracPageChanged,
    closeAllMenus,
    isMobile,
  } = props;
  const [artistData, setArtistData] = React.useState(artist);
  const [disabled, setDisabled] = React.useState(false);

  // TODO: DEVTEAM-845 - Integrate Save
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasChanged, setHasChanged] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsSaving(false);
      setDisabled(false);
      setHasChanged(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [artist]);

  React.useEffect(() => setArtistData(artist), [artist]);

  React.useEffect(() => {
    if (isArtistTracPageChanged) {
      setHasChanged(true);
    }
  }, [isArtistTracPageChanged]);

  React.useEffect(() => {
    const isInvalidSocials = !!props.social.reduce(
      (acc: null | undefined | string, social) => {
        return acc || social.error;
      },
      undefined
    );
    const isInvalidArtist = !validateArtist(artistData);
    const isDisabledUpdate = isInvalidSocials || isInvalidArtist;

    if (isDisabledUpdate !== disabled) {
      setDisabled(isDisabledUpdate);
    }
  }, [artistData, disabled, props.social]);

  if (!(artist && artistData)) {
    return null;
  }

  const saveChanges = (isNameUpdate: boolean = false) => {
    if (!hasChanged && props.artistSiteUrl) {
      window.open(props.artistSiteUrl, '_blank');
    } else if (validateArtist(artistData)) {
      const externalIds = {
        apple: artistData.platforms?.apple
          ? undefined
          : artistData.externalIds?.apple,
        soundcloud: artistData.platforms?.soundcloud
          ? undefined
          : artistData.externalIds?.soundcloud,
        spotify: artistData.platforms?.spotify
          ? undefined
          : artistData.externalIds?.spotify,
      };
      const newArtistData: SimpleArtist = {
        ...artistData,
        externalIds: externalIds,
      };
      props.publishArtistTracPage(artistTracPage);
      props.updateArtist(newArtistData);
      updatedArtistSideEffect({
        artistData: newArtistData,
        artistTracPage,
        social: props.social,
      });
      setIsSaving(true);
      setDisabled(true);
    } else if (isNameUpdate) {
      props.updateArtist(artistData);
      updatedArtistSideEffect({
        artistData,
        artistTracPage,
        social: props.social,
      });
    }
  };

  const saveChangesSideMenu = () => {
    props.updateArtist(artistData);
    updatedArtistSideEffect({
      artistData,
      artistTracPage,
      social: props.social,
    });
    closeAllMenus();
  };

  // TODO: DEVTEAM-845- Integrate BtnText in the different states.
  const getBtnText = () => {
    if (!hasChanged) {
      return 'ARTIST_PROFILE.SETTINGS.BUTTON.PREVIEW';
    } else if (isSaving) {
      return 'ARTIST_PROFILE.SETTINGS.BUTTON.SAVED';
    }
    return 'ARTIST_PROFILE.SETTINGS.BUTTON.PUBLISH';
  };

  const handleArtistChanged = (artistProfile) => {
    setArtistData({ ...artistData, ...artistProfile });
    setHasChanged(true);
  };

  return (
    <MotionOpacity className={props.className}>
      <SidebarContainer>
        {!isMobile ? (
          <Typography
            className="mb-20"
            type="subheader"
            weight="400"
            customFont="semi-bold"
            id="general"
          >
            <Translate id="ARTIST_PROFILE.PROFILE.SUBITEM.GENERAL.TITLE" />
          </Typography>
        ) : null}

        <ArtistSettingsGeneral
          hasTitle={<Translate id="ARTIST.EDITOR.TITLE" />}
          name={artistData.name}
          type={artistData.type}
          avatar={artistData.avatar}
          currentSubscriptions={currentSubscriptions}
          noHint={true}
          externalIds={artistData.externalIds}
          platforms={artistData.platforms}
          showErrors={true}
          onChange={handleArtistChanged}
          onSaveChanges={saveChangesSideMenu}
          artistId={artist.id}
        />

        <SitePreview
          previewShowing="general"
          btnText={getBtnText()}
          onSaveChanges={saveChanges}
          isDisabled={disabled}
          hideBtn={isMobile}
        />
      </SidebarContainer>
      {isMobile ? (
        <ButtonFixed fixed className="fixed-btn">
          <Button disabled={disabled} onClick={() => saveChanges()}>
            <Translate id={getBtnText()} />
          </Button>
        </ButtonFixed>
      ) : null}
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ArtistProfileInfoPage)`
  margin-bottom: 100px;
  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    margin-bottom: 0;
  }

  .fixed-btn {
    z-index: ${({ theme }) => theme.zIndices.header};
  }
`);

const SidebarContainer = styled.div`
  position: relative;
  max-width: none;
  width: auto;
  margin: 30px auto 0;
  min-height: calc(100vh - ${({ theme }) => theme.sizes.navbarMobile});
  @media ${({ theme }) => theme.device.tablet} {
    min-height: calc(100vh - ${({ theme }) => theme.sizes.navbar});
  }
`;
